import type { MultiPolygon, Polygon } from "geojson";
import {
  getGridLinesInfo,
  getStripSnapPositions,
  calculateSmartStripOffsets,
} from "../../../maps/utils/abLine";

/**
 * A stable signature of an AB line for change detection — its endpoint
 * coordinates plus its grid width. When this changes, any strip offsets computed
 * against the previous line are stale.
 */
export const abLineStripSignature = (
  coordinates: number[][] | null | undefined,
  gridWidth: number | null | undefined
): string => `${JSON.stringify(coordinates ?? null)}|${gridWidth ?? ""}`;

/**
 * Whether two offset lists are the same set of strip lines (same count, each
 * within `toleranceM` metres). Used to tell an already-correct offset set (fresh
 * apply, or a manual divider drag that still fits the field) from a stale one
 * that must be rebuilt — so we only redraw when the offsets have genuinely
 * drifted off the current field, never on top of a good configuration.
 */
export const offsetsAreEquivalent = (
  a: number[] | null | undefined,
  b: number[] | null | undefined,
  toleranceM = 1
): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every(
    (value, i) => Math.abs(value - sortedB[i]) <= toleranceM
  );
};

/**
 * Redistribute `count` strips across the field for the given AB line, returning
 * fresh offsets. Unlike reusing stored offsets (which are anchored to the old AB
 * line and fall off the field when it moves), this picks offsets from the field's
 * own grid/grey positions so all N strips fit. Returns [] when it can't compute.
 */
export const redistributeStripOffsets = (
  count: number,
  abCoordinates: number[][] | null | undefined,
  field: Polygon | MultiPolygon | null | undefined,
  gridWidth: number
): { offsets: number[]; snapPositions: number } => {
  if (
    !Array.isArray(abCoordinates) ||
    abCoordinates.length < 2 ||
    !field ||
    count < 2
  ) {
    return { offsets: [], snapPositions: 0 };
  }
  const gridInfo = getGridLinesInfo(
    abCoordinates[0],
    abCoordinates[1],
    field as never,
    gridWidth
  );
  const snapPositions = getStripSnapPositions(gridInfo).length;
  const offsets = calculateSmartStripOffsets(count, gridInfo) ?? [];
  return { offsets, snapPositions };
};
